import { NgClass, NgStyle } from '@angular/common';
import { Component, inject, input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EMPTY, switchMap, throwError } from 'rxjs';
import { DIALOG_BUTTONS } from 'src/app/shared/constants';
import { DialogComponent } from 'src/app/shared/dialogs';
import { IfHasPermissionDirective } from 'src/app/shared/directives/if-has-permission.directive';
import { AdminProject } from 'src/app/shared/models';
import { IPageable } from 'src/app/shared/models/pageable.model';
import { AlertService, ProjectService } from 'src/app/shared/services';
import { errorMessage, sanitizeQueryParams } from 'src/app/shared/utils';

@Component({
  selector: 'app-projects',
  templateUrl: './projects.component.html',
  styleUrls: ['./projects.component.scss'],
  imports: [
    FormsModule,
    NgClass,
    NgStyle,
    RouterLink,
    TranslateModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatTooltipModule,
    MatPaginatorModule,
    IfHasPermissionDirective,
  ],
})
export class ProjectsComponent implements OnInit {
  private dialog = inject(MatDialog);
  route = inject(ActivatedRoute);
  private router = inject(Router);
  private projectService = inject(ProjectService);
  private alertService = inject(AlertService);
  private translate = inject(TranslateService);

  readonly isAdmin = input(false);
  readonly isLead = input(false);

  newProjectName = '';
  projects: AdminProject[] = [];
  itemCount = 0;
  loading = true;

  displayedColumns = [
    'name',
    'manager',
    'lead',
    'engineeringManager',
    'employeesCount',
    'billingType',
  ];

  ngOnInit(): void {
    if (this.isAdmin() || this.isLead()) {
      this.displayedColumns = ['oracleId', ...this.displayedColumns, 'actions'];
    }

    this.route.queryParams.subscribe((params: IPageable) => {
      this.loading = true;
      this.projectService
        .loadProjects(params, this.isAdmin() || this.isLead())
        .subscribe((result) => {
          this.projects = result.items;
          this.itemCount = result.totalElements;
          this.loading = false;
        });
    });
  }

  applyFilter(filters: Partial<IPageable> = {}, timestamp?: number): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: sanitizeQueryParams(
        { ...this.route.snapshot.queryParams, ...filters },
        timestamp,
      ),
    });
  }

  searchProject(name: string): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: sanitizeQueryParams({ name }),
    });
  }

  createNewProject() {
    if (!this.newProjectName.trim()) return;

    this.dialog
      .open(DialogComponent, {
        data: {
          title: this.translate.instant('ADMIN.PROJECTS.CREATE_CONFIRMATION'),
          description: this.translate.instant('ADMIN.PROJECTS.CREATE_CONFIRMATION_DESCRIPTION', {
            projectName: this.newProjectName,
          }),
          sharedButtonClass: DIALOG_BUTTONS.deleteButton,
          sharedButtonText: this.translate.instant('ADMIN.PROJECTS.CREATE_BUTTON'),
        },
      })
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (!result) return EMPTY;

          return this.projectService.createProject(this.newProjectName);
        }),
      )
      .subscribe({
        next: () => {
          this.alertService.showSuccess('ADMIN.PROJECTS.SUCCESSFULLY_ADDED');
          this.searchProject(this.newProjectName);
          this.newProjectName = '';
        },
        error: (err) => this.alertService.showError(errorMessage(err)),
      });
  }

  deleteDialog(project: AdminProject) {
    this.dialog
      .open(DialogComponent, {
        data: {
          title: this.translate.instant('ADMIN.PROJECTS.DELETE_CONFIRMATION'),
          description: this.translate.instant('ADMIN.PROJECTS.DELETE_CONFIRMATION_DESCRIPTION'),
          sharedButtonClass: DIALOG_BUTTONS.deleteButton,
          sharedButtonText: this.translate.instant('ADMIN.PROJECTS.DELETE_BUTTON'),
        },
      })
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (!result) return EMPTY;

          if (project.employeesCount) {
            return throwError(() => ({
              error: this.translate.instant('ADMIN.PROJECTS.NO_DELETING_NON_EMPTY_PROJECT'),
            }));
          }
          return this.projectService.deleteProjectById(project.id);
        }),
      )
      .subscribe({
        next: () => {
          this.alertService.showSuccess('ADMIN.PROJECTS.SUCCESSFULLY_REMOVED');
          this.applyFilter(this.route.snapshot.queryParams, Date.now());
        },
        error: (err) => this.alertService.showError(errorMessage(err)),
      });
  }

  archiveDialog(project: AdminProject) {
    this.dialog
      .open(DialogComponent, {
        data: {
          title: this.translate.instant('ADMIN.PROJECTS.ARCHIVE_CONFIRMATION'),
          description: this.translate.instant('ADMIN.PROJECTS.ARCHIVE_CONFIRMATION_DESCRIPTION'),
          sharedButtonClass: DIALOG_BUTTONS.deleteButton,
          sharedButtonText: this.translate.instant('ADMIN.PROJECTS.ARCHIVE_BUTTON'),
        },
      })
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (!result) return EMPTY;

          return this.projectService.setArchivedByProjectId(project.id, true);
        }),
      )
      .subscribe({
        next: () => {
          this.alertService.showSuccess('ADMIN.PROJECTS.SUCCESSFULLY_ARCHIVED');
          this.applyFilter(this.route.snapshot.queryParams, Date.now());
        },
        error: (err) => this.alertService.showError(errorMessage(err)),
      });
  }

  unArchiveDialog(project: AdminProject) {
    this.dialog
      .open(DialogComponent, {
        data: {
          title: this.translate.instant('ADMIN.PROJECTS.UNARCHIVE_CONFIRMATION'),
          description: this.translate.instant('ADMIN.PROJECTS.UNARCHIVE_CONFIRMATION_DESCRIPTION'),
          sharedButtonClass: DIALOG_BUTTONS.deleteButton,
          sharedButtonText: this.translate.instant('ADMIN.PROJECTS.UNARCHIVE_BUTTON'),
        },
      })
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (!result) return EMPTY;

          return this.projectService.setArchivedByProjectId(project.id, false);
        }),
      )
      .subscribe({
        next: () => {
          this.alertService.showSuccess('ADMIN.PROJECTS.SUCCESSFULLY_UNARCHIVED');
          this.applyFilter(this.route.snapshot.queryParams, Date.now());
        },
        error: (err) => this.alertService.showError(errorMessage(err)),
      });
  }
}
